import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserLayouts, deleteLayout as deleteLayoutFromDB, listAllLayouts, createLayout as createLayoutInDB, listLayoutsByEventAdmin } from '../services/layoutsService';
import { getUserProfile, listUserProfiles } from '../services/profilesService';
import './Home.css';

const LayoutAdmin = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [savedLayouts, setSavedLayouts] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [profilesMap, setProfilesMap] = useState({});
  const [showNewLayoutForm, setShowNewLayoutForm] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Helpers to robustly format Firestore Timestamp | ISO string | number
  const toMillis = (ts) => {
    try {
      if (!ts) return null;
      if (typeof ts.toMillis === 'function') return ts.toMillis();
      if (ts.seconds) return ts.seconds * 1000;
      if (typeof ts === 'string' || typeof ts === 'number' || ts instanceof Date) {
        const d = new Date(ts);
        const ms = d.getTime();
        return isNaN(ms) ? null : ms;
      }
    } catch {}
    return null;
  };
  const formatDate = (ts, withTime = false) => {
    const ms = toMillis(ts);
    if (ms == null) return '-';
    try {
      const d = new Date(ms);
      return withTime ? d.toLocaleString() : d.toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const getProfileName = (uid) => {
    if (!uid) return '-';
    const p = profilesMap[uid];
    if (!p) return uid;
    const name = `${(p.firstName || '').trim()} ${(p.lastName || '').trim()}`.trim();
    return name || p.email || uid;
  };

  const toggleSort = (key) => {
    if (sortBy === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir('asc');
    }
  };

  const sortedLayouts = React.useMemo(() => {
    const arr = Array.isArray(savedLayouts) ? [...savedLayouts] : [];
    const multiplier = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      if (sortBy === 'name') {
        const an = (a.name || '').toLowerCase();
        const bn = (b.name || '').toLowerCase();
        return an < bn ? -1 * multiplier : an > bn ? 1 * multiplier : 0;
      }
      if (sortBy === 'createdAt') {
        const am = toMillis(a.createdAt) || 0;
        const bm = toMillis(b.createdAt) || 0;
        return (am - bm) * multiplier;
      }
      if (sortBy === 'updatedAt') {
        const am = toMillis(a.updatedAt) || 0;
        const bm = toMillis(b.updatedAt) || 0;
        return (am - bm) * multiplier;
      }
      if (sortBy === 'status') {
        const as = (a.status || '').toLowerCase();
        const bs = (b.status || '').toLowerCase();
        return as < bs ? -1 * multiplier : as > bs ? 1 * multiplier : 0;
      }
      if (sortBy === 'createdBy') {
        const an = (getProfileName(a.createdByUid || a.userId) || '').toLowerCase();
        const bn = (getProfileName(b.createdByUid || b.userId) || '').toLowerCase();
        return an < bn ? -1 * multiplier : an > bn ? 1 * multiplier : 0;
      }
      if (sortBy === 'updatedBy') {
        const an = (getProfileName(a.updatedByUid || a.userId) || '').toLowerCase();
        const bn = (getProfileName(b.updatedByUid || b.userId) || '').toLowerCase();
        return an < bn ? -1 * multiplier : an > bn ? 1 * multiplier : 0;
      }
      return 0;
    });
    return arr;
  }, [savedLayouts, sortBy, sortDir]);

  useEffect(() => {
    const loadLayouts = async () => {
      if (!currentUser) { setLoading(false); return; }
      try {
        setLoading(true);
        setError(null);
        setIsRetrying(false);
        setRetryCount(0);
        // Determine role
        let prof = null;
        try { prof = await getUserProfile(currentUser.uid); } catch {}
        setUserProfile(prof);
        const role = prof?.userType;
        let layouts;
        if (role === 'Admin' || role === 'Organizer') {
          layouts = await listAllLayouts();
        } else if (role === 'EventAdmin') {
          layouts = await listLayoutsByEventAdmin(currentUser.uid);
        } else {
          layouts = await getUserLayouts(currentUser.uid);
        }
        // Load profiles map for name resolution
        try {
          const allProfiles = await listUserProfiles();
          const map = {};
          for (const p of allProfiles) map[p.id] = p;
          setProfilesMap(map);
        } catch {}
        setSavedLayouts(layouts);
      } catch (err) {
        if (err.code === 'unavailable' || err.message?.includes('network') || err.message?.includes('Connection failed')) {
          setError('Network connection issue. Trying to reconnect...');
                if (retryCount < 2) {
            setIsRetrying(true);
            setTimeout(async () => {
              try {
                const prof = await getUserProfile(currentUser.uid);
                setUserProfile(prof);
                const role = prof?.userType;
                let layouts;
                if (role === 'Admin' || role === 'Organizer') {
                  layouts = await listAllLayouts();
                } else if (role === 'EventAdmin') {
                  layouts = await listLayoutsByEventAdmin(currentUser.uid);
                } else {
                  layouts = await getUserLayouts(currentUser.uid);
                }
                try {
                  const allProfiles = await listUserProfiles();
                  const map = {};
                  for (const p of allProfiles) map[p.id] = p;
                  setProfilesMap(map);
                } catch {}
                setSavedLayouts(layouts);
                setIsRetrying(false);
                setRetryCount(0);
                setError(null);
              } catch (retryErr) {
                setError('Connection issue persists. Please check your internet and try again.');
                setIsRetrying(false);
              } finally {
                setLoading(false);
              }
            }, 2000);
          }
        } else {
          setError(`Failed to load layouts: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };
    loadLayouts();
  }, [currentUser]);

  const handleCreateNewLayout = () => {
    if (newLayoutName.trim()) {
      navigate('/layout-builder', { state: { newLayoutName: newLayoutName.trim() } });
    } else {
      navigate('/layout-builder');
    }
  };

  const handleOpenLayout = (layout) => {
    navigate('/layout-builder', { state: { existingLayout: layout } });
  };

  const deleteLayout = async (layoutId, layoutName) => {
    if (window.confirm(`Are you sure you want to delete "${layoutName}"?`)) {
      try {
        await deleteLayoutFromDB(layoutId, currentUser.uid);
        const updatedLayouts = savedLayouts.filter(layout => layout.id !== layoutId);
        setSavedLayouts(updatedLayouts);
      } catch (err) {
        console.error('Error deleting layout:', err);
        alert('Failed to delete layout. Please try again.');
      }
    }
  };

  const duplicateLayout = async (layout) => {
    try {
      if (!currentUser) {
        alert('You must be logged in to duplicate layouts');
        return;
      }
      // Build copy payload from existing layout object
      const base = { ...layout };
      // Force Draft status and clean identifiers/audit fields (will be set by create)
      const copyName = base.name?.endsWith(' (Copy)')
        ? `${base.name} ${new Date().toLocaleTimeString()}`
        : `${base.name || 'Layout'} (Copy)`;
      const copyData = {
        ...base,
        name: copyName,
        status: 'Draft',
      };

      // Remove read-only / server-side fields
      delete copyData.id;
      delete copyData.createdAt;
      delete copyData.updatedAt;
      delete copyData.createdByUid;
      delete copyData.updatedByUid;
      delete copyData.userId;

      const docRef = await createLayoutInDB(copyData, currentUser.uid);
      const savedCopy = { ...copyData, id: docRef.id, createdByUid: currentUser.uid, updatedByUid: currentUser.uid };
      setSavedLayouts(prev => [savedCopy, ...prev]);
      alert(`Duplicated as Draft: "${copyName}"`);
    } catch (err) {
      console.error('Error duplicating layout:', err);
      alert('Failed to duplicate layout. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Draft': return '#f39c12';
      case 'Published': return '#27ae60';
      case 'Inactive': return '#95a5a6';
      default: return '#f39c12';
    }
  };

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>Layouts Administration</h1>
        <div className="header-actions">
          <button onClick={() => navigate('/')} className="cancel-btn">Close</button>
        </div>
      </div>

      <div className="layouts-section">
        <div className="section-header">
          <h3>📋 {userProfile?.userType === 'Admin' || userProfile?.userType === 'Organizer' ? 'All Layouts' : 'Your Layouts'}</h3>
          <div className="section-actions">
            <button 
              onClick={() => setShowNewLayoutForm(!showNewLayoutForm)} 
              className="new-layout-btn"
            >
              + New Layout
            </button>
            <button 
              onClick={async () => {
                if (currentUser) {
                  setLoading(true);
                  setError(null);
                  setIsRetrying(false);
                  try {
                    const prof = await getUserProfile(currentUser.uid);
                    setUserProfile(prof);
                    const role = prof?.userType;
                    let layouts;
                    if (role === 'Admin' || role === 'Organizer') {
                      layouts = await listAllLayouts();
                    } else if (role === 'EventAdmin') {
                      layouts = await listLayoutsByEventAdmin(currentUser.uid);
                    } else {
                      layouts = await getUserLayouts(currentUser.uid);
                    }
                    try {
                      const allProfiles = await listUserProfiles();
                      const map = {};
                      for (const p of allProfiles) map[p.id] = p;
                      setProfilesMap(map);
                    } catch {}
                    setSavedLayouts(layouts);
                  } catch (err) {
                    if (err.code === 'unavailable' || err.message?.includes('network')) {
                      setError('Network connection issue. Please check your internet connection and try again.');
                    } else {
                      setError(`Failed to load layouts: ${err.message || 'Unknown error'}`);
                    }
                  } finally {
                    setLoading(false);
                  }
                }
              }}
              className="refresh-btn"
              disabled={loading}
            >
              🔄 Refresh
            </button>
          </div>
        </div>

        {showNewLayoutForm && (
          <div className="new-layout-form">
            <input
              type="text"
              placeholder="Enter layout name (optional)..."
              value={newLayoutName}
              onChange={(e) => setNewLayoutName(e.target.value)}
              className="layout-name-input"
            />
            <div className="form-actions">
              <button onClick={handleCreateNewLayout} className="create-btn">
                Create Layout
              </button>
              <button 
                onClick={() => {
                  setShowNewLayoutForm(false);
                  setNewLayoutName('');
                }} 
                className="cancel-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="layouts-table-wrapper">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner">⏳</div>
              <p>{isRetrying ? `Retrying connection... (attempt ${retryCount + 1})` : 'Loading your layouts...'}</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <h4>Error Loading Layouts</h4>
              <p>{error}</p>
              <button onClick={() => window.location.reload()} className="retry-btn">
                Retry
              </button>
            </div>
          ) : savedLayouts.length === 0 ? (
            <div className="no-layouts">
              <div className="empty-state">
                <div className="empty-icon">📋</div>
                <h4>No layouts yet</h4>
                <p>Create your first layout to get started with the layout builder.</p>
                <button onClick={() => setShowNewLayoutForm(true)} className="start-btn">
                  Create First Layout
                </button>
              </div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="events-table layouts-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Actions</th>
                    <th onClick={() => toggleSort('name')} className={`sortable-header ${sortBy === 'name' ? 'active' : ''}`}>Name <span className="sort-indicator">{sortBy === 'name' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span></th>
                    <th>ID</th>
                    <th onClick={() => toggleSort('status')} className={`sortable-header ${sortBy === 'status' ? 'active' : ''}`}>Status <span className="sort-indicator">{sortBy === 'status' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span></th>
                    <th>Grid</th>
                    <th onClick={() => toggleSort('createdAt')} className={`sortable-header ${sortBy === 'createdAt' ? 'active' : ''}`}>Created <span className="sort-indicator">{sortBy === 'createdAt' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span></th>
                    <th onClick={() => toggleSort('createdBy')} className={`sortable-header ${sortBy === 'createdBy' ? 'active' : ''}`}>Created By <span className="sort-indicator">{sortBy === 'createdBy' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span></th>
                    <th onClick={() => toggleSort('updatedAt')} className={`sortable-header ${sortBy === 'updatedAt' ? 'active' : ''}`}>Last Updated <span className="sort-indicator">{sortBy === 'updatedAt' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span></th>
                    <th onClick={() => toggleSort('updatedBy')} className={`sortable-header ${sortBy === 'updatedBy' ? 'active' : ''}`}>Updated By <span className="sort-indicator">{sortBy === 'updatedBy' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {sortedLayouts.map(layout => (
                    <tr key={layout.id}>
                      <td style={{ padding: '8px', borderTop: '1px solid #eee' }}>
                        <div className="icon-actions">
                          <button type="button" className="icon-btn edit" aria-label="Edit" data-tooltip="Edit" onClick={() => handleOpenLayout(layout)}>✏️</button>
                          <button type="button" className="icon-btn duplicate" aria-label="Duplicate" data-tooltip="Duplicate" onClick={() => duplicateLayout(layout)}>📄</button>
                          <button type="button" className="icon-btn delete" aria-label="Delete" data-tooltip="Delete" onClick={() => deleteLayout(layout.id, layout.name)}>🗑️</button>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <span style={{ fontWeight: 600, color: '#213547' }}>{layout.name}</span>
                          <span style={{ fontSize: 12, color: '#666' }}>{layout.description || ''}</span>
                        </div>
                      </td>
                      <td style={{ fontSize: 12, color: '#666' }}>{layout.id}</td>
                      <td>
                        <span
                          className="layout-status-badge"
                          style={{ backgroundColor: getStatusColor(layout.status), padding: '0.15rem 0.6rem', borderRadius: 8 }}
                        >
                          {layout.status}
                        </span>
                      </td>
                      <td style={{ fontSize: 13, color: '#666' }}>{layout.gridSettings?.rows}×{layout.gridSettings?.columns}</td>
                      <td style={{ fontSize: 12, color: '#666' }}>{formatDate(layout.createdAt, true)}</td>
                      <td style={{ fontSize: 12, color: '#666' }}>{getProfileName(layout.createdByUid || layout.userId)}</td>
                      <td style={{ fontSize: 12, color: '#666' }}>{formatDate(layout.updatedAt, true)}</td>
                      <td style={{ fontSize: 12, color: '#666' }}>{getProfileName(layout.updatedByUid || layout.userId)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LayoutAdmin;
